import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import './HomePage.css';

// GORAI as a full acrostic — each letter anchors a set of words that spell
// downward from it. One word is revealed at a time.
const ACROSTIC = {
  G: ['Golang', 'GPIO', 'Gyro'],
  O: ['Open', 'Observability'],
  R: ['Robotics', 'Rust', 'Radio', 'Replay'],
  A: ['Autonomous', 'Agents'],
  I: ['Inference', 'IoT'],
};
const ACROSTIC_LETTERS = ['G', 'O', 'R', 'A', 'I'];
const WORD_MS = 2200;
const PAUSE_MS = 700;

const HomePage = () => {
  const { scrollYProgress } = useScroll();
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);

  // One letter reveals a word at a time. Start with Golang, then keep going with
  // a randomly chosen letter/word (never the same word twice in a row). `null`
  // is the brief pause between words where nothing descends.
  const [activeWord, setActiveWord] = useState({ letter: 'G', word: ACROSTIC.G[0] });
  useEffect(() => {
    let step = 0;
    let timer;
    let lastWord = null;
    const pickRandom = () => {
      const letter = ACROSTIC_LETTERS[Math.floor(Math.random() * ACROSTIC_LETTERS.length)];
      const list = ACROSTIC[letter];
      return { letter, word: list[Math.floor(Math.random() * list.length)] };
    };
    const run = () => {
      let next;
      if (step < ACROSTIC_LETTERS.length) {
        // first pass: reveal one word per letter, G O R A I in order
        const letter = ACROSTIC_LETTERS[step];
        next = { letter, word: ACROSTIC[letter][0] };
      } else {
        next = pickRandom();
      }
      // never show the same word twice in a row
      while (next.word === lastWord) next = pickRandom();
      lastWord = next.word;
      setActiveWord(next);
      timer = setTimeout(() => {
        setActiveWord(null);
        timer = setTimeout(() => {
          step += 1;
          run();
        }, PAUSE_MS);
      }, WORD_MS);
    };
    run();
    return () => clearTimeout(timer);
  }, []);

  // Renders one GORAI letter: its anchor plus the word (if any) descending from
  // it. Returns elements (not a nested component) so AnimatePresence isn't
  // remounted on every render.
  const renderAcrosticLetter = (letter, colorClass) => (
    <span className="gorai-col" aria-hidden="true">
      <span className={`gorai-letter ${colorClass}`}>{letter}</span>
      <span className="gorai-descend">
        <AnimatePresence mode="wait">
          {activeWord?.letter === letter && (
            <motion.span
              key={activeWord.word}
              className="gorai-descend-word"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.35 }}
            >
              {activeWord.word
                .slice(1)
                .split('')
                .map((ch, i) => (
                  <span key={i} className="gorai-descend-letter">
                    {ch}
                  </span>
                ))}
            </motion.span>
          )}
        </AnimatePresence>
      </span>
    </span>
  );

  const founders = [
    {
      name: 'Greg Herlein',
      role: 'Co-Founder',
      image: '/images/founders/greg.jpg',
      linkedin: 'https://www.linkedin.com/in/gherlein/',
      bio: 'Former US Navy Submarine Nuclear Power Plant Operator and Supervisor. Decades of Silicon Valley experience leading engineering teams at Rackspace, Cisco, and AWS. Passionate robotics coach for FIRST LEGO League and VEX teams.'
    },
    {
      name: 'Luca Herlein',
      role: 'Co-Founder',
      image: '/images/founders/luca.png',
      linkedin: 'https://www.linkedin.com/in/luca-herlein-4023711ba/',
      bio: 'Eight years of competition robotics from FIRST LEGO League World Championships through VEX. Aerospace Engineering at CU Boulder, Aerodynamics Lead on the 2021-22 Design Build Fly competition team.'
    }
  ];

  return (
    <div className="home-page">
      {/* Animated Background */}
      <motion.div className="home-bg" style={{ y: backgroundY }}>
        <div className="bg-gradient" />
        <div className="grid-pattern" />
        <div className="glow-orb orb-1" />
        <div className="glow-orb orb-2" />
        <div className="glow-orb orb-3" />
      </motion.div>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-container">
        <div className="hero-content">
          <div className="hero-headline-group">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            <span className="hero-line">The Open Platform for</span>
            <span className="hero-line accent">the Physical AI Future</span>
          </motion.h1>

          <motion.div
            className="hero-gorai"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            aria-label={`GORAI — ${ACROSTIC_LETTERS.map((l) => `${l}: ${ACROSTIC[l].join(', ')}`).join('; ')}`}
          >
            {renderAcrosticLetter('G', 'gorai-go')}
            {renderAcrosticLetter('O', 'gorai-go')}
            {renderAcrosticLetter('R', 'gorai-r')}
            {renderAcrosticLetter('A', 'gorai-ai')}
            {renderAcrosticLetter('I', 'gorai-ai')}
          </motion.div>
          </div>
        </div>

        <motion.div
          className="hero-visual"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4, duration: 0.8 }}
        >
          <div className="visual-container">
            <div className="visual-glow" />
            <div className="visual-rings">
              <div className="ring ring-1" />
              <div className="ring ring-2" />
              <div className="ring ring-3" />
            </div>
            <div className="visual-core">
              <img src="/er-logo.svg" alt="Emerging Robotics" className="hero-logo-img" />
            </div>
          </div>
        </motion.div>
        </div>

        <div className="scroll-indicator">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <span className="scroll-arrow">↓</span>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="mission-section" id="mission">
        <div className="section-container">
          <motion.div
            className="mission-content"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <span className="section-label">Our Mission</span>
            <h2>Building for the AI Era</h2>
            <p className="mission-text">
              Robotics is shifting. Decision-making is moving up the stack—from hand-authored 
              logic to AI-driven autonomy. The future of robotics isn't just better hardware, 
              it's systems where behavior, coordination, and safety are designed for AI from the start.
            </p>
            <p className="mission-text">
              <strong>We're building the platform for that future.</strong> Open-source robot platforms, 
              frameworks, and complete robots that treat AI agents, replay, and fleet operations as 
              foundational—not bolt-ons. Built to be approachable, operational, and ready to scale.
            </p>
          </motion.div>

          <div className="values-grid">
            {[
              { 
                icon: '🔓', 
                title: 'Radically Open', 
                desc: 'Every CAD file, every line of code, every decision—documented and available.' 
              },
              { 
                icon: '🔧', 
                title: 'Maker-First', 
                desc: 'Designed for 3D printers, off-the-shelf parts, and real-world assembly.' 
              },
              { 
                icon: '💪', 
                title: 'No Compromises', 
                desc: 'Professional capability at maker prices. Open doesn\'t mean weak.' 
              },
              { 
                icon: '🌍', 
                title: 'Community Driven', 
                desc: 'Built with the community, for the community. Your input shapes our direction.' 
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                className="value-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
              >
                <span className="value-icon">{item.icon}</span>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="products-section">
        <div className="section-container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="section-label">Our Products</span>
            <h2>What We're Building</h2>
            <p className="section-subtitle">
              Open-source robotics platforms designed for real-world deployment.
            </p>
          </motion.div>

          <div className="products-grid">
            <motion.div
              className="product-card featured gorai-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="product-status">In Development</div>
              <div className="product-content">
                <div className="product-icon-img">
                  <img src="/images/gorai.png" alt="Gorai" />
                </div>
                <h3>Gorai</h3>
                <p className="product-tagline">The Robotics Platform for the AI Era</p>
                <p className="product-desc">
                  Agent-compatible. Not agent-dependent. Gorai treats AI-driven execution, 
                  action logs, and replay as first-class platform concerns. Build robots like 
                  software. Run them like systems. Productive in days, not months.
                </p>
                <ul className="product-features">
                  <li>AI agents as first-class executors</li>
                  <li>Replay and auditability built-in</li>
                  <li>Fleet operations without rewriting</li>
                  <li>Single binary to distributed systems</li>
                </ul>
                <Link to="/gorai" className="product-link gorai-link">
                  Explore Gorai →
                </Link>
              </div>
              <div className="product-visual gorai-visual">
                <img 
                  src="/images/gorai.png" 
                  alt="Gorai Robotics Framework"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Founders Section */}
      <section className="founders-section">
        <div className="section-container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="section-label">The Team</span>
            <h2>Meet the Founders</h2>
            <p className="section-subtitle">
              A father-son team combining decades of industry experience with competition robotics expertise.
            </p>
          </motion.div>

          <div className="founders-grid">
            {founders.map((founder, index) => (
              <motion.div
                key={founder.name}
                className="founder-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15, duration: 0.5 }}
              >
                <div className="founder-image">
                  <img 
                    src={founder.image} 
                    alt={founder.name}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="founder-image-placeholder">
                    <span>{founder.name.split(' ').map(n => n[0]).join('')}</span>
                  </div>
                </div>
                <div className="founder-info">
                  <h3>{founder.name}</h3>
                  <span className="founder-role">{founder.role}</span>
                  <a 
                    href={founder.linkedin} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="founder-linkedin"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                    LinkedIn
                  </a>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            className="founders-cta"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <Link to="/founders" className="learn-more-link">
              Learn more about our story →
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="section-container">
          <motion.div
            className="cta-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Join the Movement</h2>
            <p>
              Whether you're a maker, researcher, or just curious—we'd love to have you. 
              Follow our progress, contribute to our projects, or build your own.
            </p>
            <div className="cta-buttons">
              <a 
                href="https://github.com/emergingrobotics/gorai" 
                target="_blank" 
                rel="noopener noreferrer"
                className="cta-primary"
              >
                <span>⭐ Star on GitHub</span>
              </a>
              <Link to="/gorai" className="cta-secondary">
                Explore Gorai
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="home-footer">
        <div className="section-container">
          <div className="footer-content">
            <div className="footer-brand">
              <span className="footer-logo">
                <img src="/er-logo.svg" alt="" className="footer-logo-img" />
                Emerging Robotics
              </span>
              <p>The robot platform for the physical AI future.</p>
            </div>
            <div className="footer-links">
              <div className="footer-col">
                <h4>Products</h4>
                <Link to="/gorai">Gorai</Link>
              </div>
              <div className="footer-col">
                <h4>Info</h4>
                <Link to="/founders">Founders</Link>
              </div>
              <div className="footer-col">
                <h4>Project</h4>
                <a href="https://github.com/emergingrobotics/gorai" target="_blank" rel="noopener noreferrer">GitHub</a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>© 2025-2026 Emerging Robotics. Open Source, Open Future.</p>
            <p className="licenses">Hardware: CERN-OHL-S-2.0 • Software: AGPL-3.0 • Free for non-commercial use</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
